import { Track } from "@/src/lib/tracking";
/**
 * Optimistic Update Manager - 乐观更新管理
 *
 * 负责管理乐观更新、回滚和 allFavoriteData 更新
 */

import type { FavoritesCenterState, UserData } from "../types";
import {
  batchUpdateVersionedData,
  updateVersionedData,
} from "./VersionedDataManager";

export class OptimisticUpdateManager {
  constructor(
    private state: FavoritesCenterState,
    private getCurrentUserId: () => string | null,
    private getOrCreateUserData: (userId: string) => UserData,
  ) {}

  /**
   * Rollback favorite status
   */
  rollbackFavoriteStatus(
    contentId: string,
    folderId: string,
    action: "ADD" | "REMOVE",
    clearOperationHistory: (contentId: string, folderId: string) => void,
  ): void {
    const currentState = this.state.favoriteStates.data[contentId] || [];
    let revertedState: string[];

    if (action === "ADD") {
      revertedState = currentState.filter((id: string) => id !== folderId);
    } else {
      revertedState = currentState.includes(folderId)
        ? currentState
        : [...currentState, folderId];
    }

    const newData = { ...this.state.favoriteStates.data };
    newData[contentId] = revertedState;
    updateVersionedData(this.state.favoriteStates, newData);

    const currentUserId = this.getCurrentUserId();
    if (currentUserId) {
      const userData = this.getOrCreateUserData(currentUserId);
      userData.favoriteStates.set(contentId, revertedState);
    }

    // Rollback allFavoriteData to maintain consistency
    this.updateAllFavoriteData([{ key: contentId, value: revertedState }]);

    // Clear operation history
    clearOperationHistory(contentId, folderId);
  }

  /**
   * Update allFavoriteData summary data
   */
  updateAllFavoriteData(updates: Array<{ key: string; value: any }>): void {
    // If allFavoriteData is not initialized, create it first
    if (!this.state.allFavoriteData) {
      this.state.allFavoriteData = {
        data: {},
        version: 0,
        lastUpdated: 0,
      };
    }

    const newAllFavoriteData = { ...this.state.allFavoriteData.data };

    updates.forEach(({ key, value }) => {
      // Use contentId directly as key
      newAllFavoriteData[key] = {
        contentId: key,
        favoriteStates: value,
        lastUpdated: Date.now(),
      };
    });

    // Update versioned data
    updateVersionedData(this.state.allFavoriteData, newAllFavoriteData, true);

    // Also update source data (cache logic)
    const currentUserId = this.getCurrentUserId();
    if (currentUserId) {
      const userData = this.getOrCreateUserData(currentUserId);
      if (!userData.allFavoriteData) {
        userData.allFavoriteData = new Map();
      }

      // Update allFavoriteData in source data
      updates.forEach(({ key, value }) => {
        userData.allFavoriteData!.set(key, {
          contentId: key,
          favoriteStates: value,
          lastUpdated: Date.now(),
        });
      });

      userData.lastAccess = Date.now();
    }
  }

  /**
   * Batch update favorite states
   */
  batchUpdateFavoriteStates(
    updates: Array<{ key: string; value: string[] }>,
  ): void {
    // Update source data
    const currentUserId = this.getCurrentUserId();
    if (currentUserId) {
      const userData = this.getOrCreateUserData(currentUserId);
      updates.forEach(({ key, value }) => {
        userData.favoriteStates.set(key, value);
      });
      userData.lastAccess = Date.now();
    }

    // Batch update active data
    batchUpdateVersionedData(this.state.favoriteStates, updates, true);

    // Update allFavoriteData
    this.updateAllFavoriteData(updates);
  }

  /**
   * Mark assets as unfavorited (failure fallback)
   */
  markAsUnfavorited(contentIds: string[]): void {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId || contentIds.length === 0) {
      return;
    }

    const userData = this.getOrCreateUserData(currentUserId);

    // Batch update as empty array (unfavorited)
    const updates = contentIds.map((contentId) => ({
      key: contentId,
      value: [] as string[],
    }));

    // Update source data
    updates.forEach(({ key, value }) => {
      userData.favoriteStates.set(key, value);
    });

    // Batch update active data
    batchUpdateVersionedData(this.state.favoriteStates, updates, true);

    // Update allFavoriteData summary data
    this.updateAllFavoriteData(updates);

    userData.lastAccess = Date.now();

    Track.log(
      "info",
      "OptimisticUpdateManager",
      `Failure fallback: ${contentIds.length} assets marked as unfavorited`,
    );
  }
}
